import React, { useState } from "react";
import { useAuth } from "./auth-context";
import { Link } from 'react-router-dom';
import {
    Brain,
    Lock,
    Infinity,
    Files,
    Timer,
    Target,
    Clock,
    XCircle,
    Layers,
    RefreshCw,
    Zap,
    ArrowRight,
    Info,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

export const DomainSelector = ({ domains, onSelect, onClose }) => (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
        <div className="rounded-2xl bg-[#0B1224] ring-1 ring-white/10 max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-blue-400 mb-4">Select Domain</h3>
            <div className="space-y-3">
                {Object.entries(domains).map(([domain, weight]) => (
                    <button
                        key={domain}
                        onClick={() => onSelect(domain)}
                        className="w-full text-left p-4 rounded-xl bg-white/5 ring-1 ring-white/10 hover:bg-white/10 transition-colors"
                    >
                        <div className="font-medium text-white">{domain}</div>
                        <div className="text-sm text-white/55">Weight: {(weight * 100).toFixed()}%</div>
                    </button>
                ))}
            </div>
            <button
                onClick={onClose}
                className="mt-4 w-full p-2 rounded-xl ring-1 ring-white/10 text-white/55 hover:bg-white/10"
            >
                Cancel
            </button>
        </div>
    </div>
);

export const PracticeMode = ({ title, description, icon: Icon, stats, onClick, isPaid, disabled, badge }) => {
    const { isPaidUser } = useAuth();
    const isLocked = (isPaid && !isPaidUser) || disabled;

    return (
        <div
            onClick={!isLocked ? onClick : undefined}
            className={`relative group transition-all duration-300
                ${isLocked ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:ring-cyan-500/30"}
                rounded-2xl bg-white/5 ring-1 ring-white/10`}
        >
            <div className="p-6">
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className={`h-12 w-12 rounded-2xl flex items-center justify-center transition-colors
                            ${isLocked ? "opacity-50 bg-white/5" : "bg-blue-500/10 group-hover:bg-blue-500/20"}`}>
                            <Icon className="text-cyan-300 h-6 w-6" />
                        </div>
                        {badge && (
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${badge.className}`}>
                                {badge.text}
                            </span>
                        )}
                    </div>
                    <h3 className="text-lg font-semibold text-blue-400 mb-2">{title}</h3>
                    <p className="text-sm text-white/65">{description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    {stats.map((stat, index) => (
                        <div key={index} className="text-sm text-white/55 flex items-center gap-1">
                            {stat.icon} {stat.label}: {stat.value}
                        </div>
                    ))}
                </div>
            </div>

            {/* Lock overlay for paid modes */}
            {isPaid && !isPaidUser && (
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center rounded-lg">
                    <Lock className="text-white h-10 w-10" />
                </div>
            )}
        </div>
    );
};


// parent component that renders practice modes
const PracticeModeSelection = ({ onStartQuiz, missedQuestionCount = 0, dueItemCount = 0, dailyQuizUsed = false }) => {
    const [showDomainSelector, setShowDomainSelector] = useState(false);
    const [showAdaptiveInfo, setShowAdaptiveInfo] = useState(false);
    const { isPaidUser } = useAuth();

    // Quick Quiz is free for all users, but limited to 1 session/day for unpaid
    const quizDisabledForFree = !isPaidUser && dailyQuizUsed;

    const modes = [
        {
            title: "Quick Quiz",
            description: quizDisabledForFree
                ? "You've used your free quiz for today. Come back tomorrow or upgrade for unlimited access."
                : "20 questions covering key Security+ domains.",
            icon: Files,
            stats: [
                { label: "Questions", value: "20", icon: <Files className="h-4 w-4 text-blue-400" /> },
                { label: "Time Limit", value: "∞", icon: <Clock className="h-4 w-4 text-blue-400" /> }
            ],
            mode: 'Quiz',
            isPaid: false,
            disabled: quizDisabledForFree,
            badge: !isPaidUser
                ? (quizDisabledForFree
                    ? { text: "Used Today", className: "bg-gray-700 text-gray-400" }
                    : { text: "Free Daily", className: "bg-cyan-500/20 text-cyan-400" })
                : null
        },
        {
            title: "Smart Review",
            description: "Spaced repetition reviews items right before you forget them.",
            icon: RefreshCw,
            stats: [
                { label: "Due Now", value: dueItemCount, icon: <RefreshCw className="h-4 w-4 text-blue-400" /> },
                { label: "Method", value: "FSRS", icon: <Target className="h-4 w-4 text-blue-400" /> }
            ],
            mode: 'Review',
            isPaid: true
        },
        {
            title: "Missed Questions",
            description: "Review and practice questions you've previously answered incorrectly.",
            icon: XCircle,
            stats: [
                { label: "Available", value: missedQuestionCount, icon: <Files className="h-4 w-4 text-blue-400" /> },
                { label: "Focus", value: "Mistakes", icon: <Target className="h-4 w-4 text-blue-400" /> }
            ],
            mode: 'Missed',
            isPaid: true
        },
        {
            title: "Unlimited Mode",
            description: "Adaptive learning that adjusts to your skill level.",
            icon: Infinity,
            stats: [
                { label: "Difficulty", value: "Adaptive", icon: <Target className="h-4 w-4 text-blue-400" /> },
                { label: "Time Limit", value: "∞", icon: <Clock className="h-4 w-4 text-blue-400" /> }
            ],
            mode: 'Unlimited',
            isPaid: true
        },
        {
            title: "Exam Simulation",
            description: "Full exam experience with 90 questions in 90 minutes.",
            icon: Timer,
            stats: [
                { label: "Questions", value: "90", icon: <Files className="h-4 w-4 text-blue-400" /> },
                { label: "Time Limit", value: "90 min", icon: <Clock className="h-4 w-4 text-blue-400" /> }
            ],
            mode: 'Exam',
            isPaid: true
        },
        {
            title: "Domain Practice",
            description: "Focus on specific exam domains to strengthen weak areas.",
            icon: Layers,
            stats: [
                { label: "Domains", value: "5", icon: <Layers className="h-4 w-4 text-blue-400" /> },
                { label: "Questions", value: "∞", icon: <Files className="h-4 w-4 text-blue-400" /> }
            ],
            mode: 'Domain',
            isPaid: true
        }
    ];

    const handleModeSelect = (mode) => {
        // Quick Quiz is free for everyone (1/day for unpaid)
        if (!isPaidUser && mode !== "Quiz") return;
        if (mode === "Quiz" && quizDisabledForFree) return;

        if (mode === "Domain") {
            setShowDomainSelector(true);
        } else {
            onStartQuiz(mode);
        }
    };


    return (
        <div className="space-y-6">
            {/* Top upgrade banner for free users */}
            {!isPaidUser && (
                <div className="bg-gradient-to-r from-cyan-900/40 to-blue-900/40 ring-1 ring-cyan-500/30 rounded-2xl p-4 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-cyan-500/60 via-blue-500/60 to-cyan-500/60"></div>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                                <Zap className="h-5 w-5 text-cyan-400" />
                            </div>
                            <div>
                                <p className="text-white font-semibold text-sm">
                                    Unlock 200+ practice questions & all training modes
                                </p>
                                <p className="text-white/55 text-xs">
                                    Exam simulations, spaced repetition, domain focus, and more
                                </p>
                            </div>
                        </div>
                        <Link
                            to="/pricing"
                            className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-sm font-semibold transition-all group flex-shrink-0"
                        >
                            Upgrade
                            <ArrowRight className="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                    </div>
                </div>
            )}

            <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">Practice Modes</h2>
                <p className="text-white/55 mb-3">Select a practice mode that matches your study goals</p>

                {/* Adaptive algorithm explanation */}
                <button
                    onClick={() => setShowAdaptiveInfo(!showAdaptiveInfo)}
                    className="inline-flex items-center gap-1.5 text-sm text-cyan-400/70 hover:text-cyan-400 transition-colors"
                >
                    <Info size={14} />
                    How does adaptive practice work?
                    {showAdaptiveInfo ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {showAdaptiveInfo && (
                    <div className="mt-3 rounded-xl bg-cyan-500/5 ring-1 ring-cyan-500/15 p-4 text-sm text-white/60 leading-relaxed space-y-2">
                        <p>
                            <strong className="text-white/80">Adaptive Question Selection:</strong> Our system uses an ability estimation model (EAP) that tracks your skill level across each of the five Security+ domains independently. After every answer, your estimated ability updates in real time, and the next question is chosen to maximize information gain. You'll always be challenged at the edge of your knowledge, not too easy and not too hard.
                        </p>
                        <p>
                            <strong className="text-white/80">Spaced Repetition:</strong> Smart Review mode uses a spaced repetition algorithm (FSRS) that schedules items for review right before you're likely to forget them. Each item tracks its own stability and difficulty, so well-known concepts are reviewed less often while tricky topics resurface more frequently.
                        </p>
                        <p>
                            <strong className="text-white/80">Domain Mastery Decay:</strong> Your mastery scores gradually decay over time if you don't practice a domain. This reflects real-world forgetting curves and keeps your progress reports honest. If you haven't studied Security Operations in two weeks, your readiness score will reflect that.
                        </p>
                    </div>
                )}
            </div>

            <div className="grid gap-6 md:grid-cols-3">
                {modes.map((mode, index) => (
                    <PracticeMode
                        key={index}
                        {...mode}
                        onClick={() => handleModeSelect(mode.mode)}
                    />
                ))}
            </div>

            {showDomainSelector && (
                <DomainSelector
                    domains={{
                        "Security Operations": 0.28,
                        "General Security Concepts": 0.12,
                        "Threats, Vulnerabilities and Mitigations": 0.22,
                        "Security Program Management": 0.20,
                        "Security Architecture": 0.18
                    }}
                    onSelect={(domain) => {
                        setShowDomainSelector(false);
                        onStartQuiz("Domain", domain);
                    }}
                    onClose={() => setShowDomainSelector(false)}
                />
            )}
        </div>
    );
};

export default PracticeModeSelection;
